import { supabase } from './supabase';

// Audit Trail Service
export const auditTrailService = {
  // Log audit event
  async logAuditEvent({
    gymId,
    userId,
    eventType,
    eventCategory,
    eventAction,
    eventDescription,
    severity = 'info',
    ipAddress = null,
    affectedResourceType = null,
    affectedResourceId = null,
    beforeValue = null,
    afterValue = null,
    metadata = {}
  }) {
    try {
      const { data, error } = await supabase?.from('audit_logs')?.insert({
          gym_id: gymId,
          user_id: userId,
          event_type: eventType,
          event_category: eventCategory,
          event_action: eventAction,
          event_description: eventDescription,
          severity,
          ip_address: ipAddress,
          affected_resource_type: affectedResourceType,
          affected_resource_id: affectedResourceId,
          before_value: beforeValue,
          after_value: afterValue,
          metadata
        })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error logging audit event:', error);
      return { data: null, error };
    }
  },

  // Get audit logs with filters
  async getAuditLogs({
    gymId = null,
    userId = null,
    eventType = null,
    severity = null,
    startDate = null,
    endDate = null,
    isSuspicious = null,
    limit = 100,
    offset = 0
  } = {}) {
    try {
      let query = supabase?.from('audit_logs')?.select(`
          *,
          user:user_profiles!audit_logs_user_id_fkey(id, full_name, email, role),
          gym:gyms(id, name),
          reviewed_by_user:user_profiles!audit_logs_reviewed_by_fkey(id, full_name)
        `, { count: 'exact' })?.order('created_at', { ascending: false });

      if (gymId) query = query?.eq('gym_id', gymId);
      if (userId) query = query?.eq('user_id', userId);
      if (eventType) query = query?.eq('event_type', eventType);
      if (severity) query = query?.eq('severity', severity);
      if (isSuspicious !== null) query = query?.eq('is_suspicious', isSuspicious);
      if (startDate) query = query?.gte('created_at', startDate);
      if (endDate) query = query?.lte('created_at', endDate);

      query = query?.range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      if (error) throw error;
      return { data, count, error: null };
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      return { data: null, count: 0, error };
    }
  },

  // Get audit statistics
  async getAuditStatistics(gymId = null, days = 30) {
    try {
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      let query = supabase?.from('audit_logs')?.select('event_type, severity, is_suspicious', { count: 'exact' })?.gte('created_at', startDate?.toISOString());

      if (gymId) query = query?.eq('gym_id', gymId);

      const { data, error, count } = await query;

      if (error) throw error;

      // Calculate statistics
      const stats = {
        totalEvents: count || 0,
        byEventType: {},
        bySeverity: {},
        suspiciousCount: 0
      };

      data?.forEach(log => {
        stats.byEventType[log.event_type] = (stats?.byEventType?.[log?.event_type] || 0) + 1;
        stats.bySeverity[log.severity] = (stats?.bySeverity?.[log?.severity] || 0) + 1;
        if (log?.is_suspicious) stats.suspiciousCount++;
      });

      return { data: stats, error: null };
    } catch (error) {
      console.error('Error fetching audit statistics:', error);
      return { data: null, error };
    }
  },

  // Log security event
  async logSecurityEvent({
    gymId,
    userId,
    eventType,
    severity,
    ipAddress,
    location = null,
    userAgent = null,
    description,
    threatIndicators = {},
    requiresImmediateAction = false
  }) {
    try {
      const { data, error } = await supabase?.from('security_events')?.insert({
          gym_id: gymId,
          user_id: userId,
          event_type: eventType,
          severity,
          ip_address: ipAddress,
          location,
          user_agent: userAgent,
          description,
          threat_indicators: threatIndicators,
          requires_immediate_action: requiresImmediateAction
        })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error logging security event:', error);
      return { data: null, error };
    }
  },

  // Get security events
  async getSecurityEvents({
    gymId = null,
    userId = null,
    eventType = null,
    severity = null,
    resolved = null,
    limit = 100,
    offset = 0
  } = {}) {
    try {
      let query = supabase?.from('security_events')?.select(`
          *,
          user:user_profiles!security_events_user_id_fkey(id, full_name, email, role),
          gym:gyms(id, name),
          resolved_by_user:user_profiles!security_events_resolved_by_fkey(id, full_name)
        `, { count: 'exact' })?.order('created_at', { ascending: false });

      if (gymId) query = query?.eq('gym_id', gymId);
      if (userId) query = query?.eq('user_id', userId);
      if (eventType) query = query?.eq('event_type', eventType);
      if (severity) query = query?.eq('severity', severity);
      if (resolved !== null) query = query?.eq('resolved', resolved);

      query = query?.range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      if (error) throw error;
      return { data, count, error: null };
    } catch (error) {
      console.error('Error fetching security events:', error);
      return { data: null, count: 0, error };
    }
  },

  // Resolve security event
  async resolveSecurityEvent(eventId, resolvedBy, resolutionNotes) {
    try {
      const { data, error } = await supabase?.from('security_events')?.update({
          resolved: true,
          resolved_by: resolvedBy,
          resolved_at: new Date()?.toISOString(),
          resolution_notes: resolutionNotes
        })?.eq('id', eventId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error resolving security event:', error);
      return { data: null, error };
    }
  },

  // Get compliance monitoring data
  async getComplianceMonitoring(gymId = null) {
    try {
      let query = supabase?.from('compliance_monitoring')?.select(`
          *,
          gym:gyms(id, name),
          assigned_to_user:user_profiles!compliance_monitoring_assigned_to_fkey(id, full_name)
        `)?.order('created_at', { ascending: false });

      if (gymId) query = query?.eq('gym_id', gymId);

      const { data, error } = await query;

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching compliance monitoring:', error);
      return { data: null, error };
    }
  },

  // Update compliance status
  async updateComplianceStatus(complianceId, updates) {
    try {
      const { data, error } = await supabase?.from('compliance_monitoring')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', complianceId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating compliance status:', error);
      return { data: null, error };
    }
  },

  // Get failed payment logs
  async getFailedPayments({
    gymId = null,
    startDate = null,
    endDate = null,
    limit = 100,
    offset = 0
  } = {}) {
    try {
      let query = supabase?.from('audit_logs')?.select(`
          *,
          user:user_profiles!audit_logs_user_id_fkey(id, full_name, email),
          gym:gyms(id, name)
        `, { count: 'exact' })?.eq('event_type', 'payment_event')?.eq('severity', 'error')?.order('created_at', { ascending: false });

      if (gymId) query = query?.eq('gym_id', gymId);
      if (startDate) query = query?.gte('created_at', startDate);
      if (endDate) query = query?.lte('created_at', endDate);

      query = query?.range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      if (error) throw error;
      return { data, count, error: null };
    } catch (error) {
      console.error('Error fetching failed payments:', error);
      return { data: null, count: 0, error };
    }
  },

  // Get admin actions
  async getAdminActions({
    gymId = null,
    userId = null,
    startDate = null,
    endDate = null,
    limit = 100,
    offset = 0
  } = {}) {
    try {
      let query = supabase?.from('audit_logs')?.select(`
          *,
          user:user_profiles!audit_logs_user_id_fkey(id, full_name, email, role),
          gym:gyms(id, name)
        `, { count: 'exact' })?.eq('event_type', 'admin_action')?.order('created_at', { ascending: false });

      if (gymId) query = query?.eq('gym_id', gymId);
      if (userId) query = query?.eq('user_id', userId);
      if (startDate) query = query?.gte('created_at', startDate);
      if (endDate) query = query?.lte('created_at', endDate);

      query = query?.range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      if (error) throw error;
      return { data, count, error: null };
    } catch (error) {
      console.error('Error fetching admin actions:', error);
      return { data: null, count: 0, error };
    }
  },

  // Get system changes
  async getSystemChanges({
    gymId = null,
    startDate = null,
    endDate = null,
    limit = 100,
    offset = 0
  } = {}) {
    try {
      let query = supabase?.from('audit_logs')?.select(`
          *,
          user:user_profiles!audit_logs_user_id_fkey(id, full_name, email, role),
          gym:gyms(id, name)
        `, { count: 'exact' })?.eq('event_type', 'system_change')?.order('created_at', { ascending: false });

      if (gymId) query = query?.eq('gym_id', gymId);
      if (startDate) query = query?.gte('created_at', startDate);
      if (endDate) query = query?.lte('created_at', endDate);

      query = query?.range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      if (error) throw error;
      return { data, count, error: null };
    } catch (error) {
      console.error('Error fetching system changes:', error);
      return { data: null, count: 0, error };
    }
  },

  // Search audit logs
  async searchAuditLogs(searchTerm, gymId = null, limit = 50) {
    try {
      let query = supabase?.from('audit_logs')?.select(`
          *,
          user:user_profiles!audit_logs_user_id_fkey(id, full_name, email),
          gym:gyms(id, name)
        `)?.or(`event_description.ilike.%${searchTerm}%,event_action.ilike.%${searchTerm}%,event_category.ilike.%${searchTerm}%`)?.order('created_at', { ascending: false })?.limit(limit);

      if (gymId) query = query?.eq('gym_id', gymId);

      const { data, error } = await query;

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error searching audit logs:', error);
      return { data: null, error };
    }
  },

  // Export audit logs
  async exportAuditLogs(filters = {}) {
    try {
      const { data, error } = await this.getAuditLogs({ ...filters, limit: 10000 });
      
      if (error) throw error;
      
      // Convert to CSV format
      const headers = ['Timestamp', 'Event Type', 'Category', 'Action', 'Description', 'Severity', 'User', 'Gym', 'IP Address'];
      const csvRows = [headers?.join(',')];
      
      data?.forEach(log => {
        const row = [
          new Date(log.created_at)?.toISOString(),
          log?.event_type,
          log?.event_category,
          log?.event_action,
          `"${log?.event_description?.replace(/"/g, '""')}"`,
          log?.severity,
          log?.user?.full_name || 'N/A',
          log?.gym?.name || 'N/A',
          log?.ip_address || 'N/A'
        ];
        csvRows?.push(row?.join(','));
      });
      
      return { data: csvRows?.join('\n'), error: null };
    } catch (error) {
      console.error('Error exporting audit logs:', error);
      return { data: null, error };
    }
  }
};

export default auditTrailService;